// Updated Dashboard.js - Fixed version
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { useSubscription } from '../../Context/SubscriptionContext';
import supabase from '../../supabase';
import './Dashboard.css';
import logo from '../../assets/logo.svg'
import Clients from './Clients';
import UserProfileItems from './UserProfileItems';
import Expenses from './Expenses';
import InvoiceForm from '../Invoice/InvoiceForm';
import ClientInvoiceTracker from '../Tracker/ClientInvoiceTracker';
import DashboardRecentActivity from './DashboardRecentActivity';
import ManualIncomeForm from './ManualIncomeForm';
import DashboardMiegraph from './DashboardMIEgraph';
import DashboardMielinegraph from './DashboardMielinegraph';
import InvoiceStatusTracker from './InvoiceStatusTracker';
import TopClientsRevenue from './TopClientsRevenue';
import DashboardInvoiceCalendar from './DashboardInvoiceCalendar';
import QuotationForm from '../Quotation/QuotationForm';
import QuoteTracker from '../QuoteTracker/QuoteTracker';
import EmployeeForm from '.././hr/EmployeeForm';
import EmployeeDashboard from '.././hr/EmployeeDashboard'
import EmployeeDetails from '.././hr/EmployeeDetails';
import UserProfileSetup from '../UserProfile/UserProfileSetup';
import ProjectForm from '../Project/ProjectForm';
import ProjectDashboard from '../Project/ProjectDashboard';
import SubscriptionBanner from '../../components/subscription/SubscriptionBanner';
import FeatureLockModal from '../../components/subscription/FeatureLockModal';

import {
  BarChart,
  FileText,
  ClipboardList,
  IndianRupee,
  Package,
  Wallet,
  Users,
  ChevronRight,
  ChevronLeft,
  UserCircle,
  ClipboardListIcon,
  User as UserIcon,
  Menu as MenuIcon,
  Lock,
  FolderKanban
} from 'lucide-react';
import QuoteAnalyticGraph from './QuoteAnalyticGraph';

// URL to Tab ID mapping
const URL_TAB_MAP = {
  '': 'general',
  'dashboard': 'general',
  'create-invoice': 'invoice-generation',
  'track-invoices': 'invoice-tracking',
  'clients': 'client-management',
  'create-quote': 'Quotations',
  'track-quotes': 'QuotationTracker',
  'earnings': 'Earnings',
  'expenses': 'expense-tracker',
  'invoice-items': 'item-management',
  'team': 'EmployeeDashboard',
  'projects': 'ProjectDashboard',
  'profile': 'user-profile-setup'
};

// Tab ID to URL mapping (reverse)
const TAB_URL_MAP = {
  'general': 'dashboard',
  'invoice-generation': 'create-invoice',
  'invoice-tracking': 'track-invoices',
  'client-management': 'clients',
  'Quotations': 'create-quote',
  'QuotationTracker': 'track-quotes',
  'Earnings': 'earnings',
  'expense-tracker': 'expenses',
  'item-management': 'invoice-items',
  'EmployeeDashboard': 'team',
  'ProjectDashboard': 'projects',
  'user-profile-setup': 'profile'
};

function SidebarProfile({ user, onProfileClick }) {
  const [avatarError, setAvatarError] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  
  useEffect(() => {
    if (user) {
      setAvatarError(false);
      const metadata = user.user_metadata || {};
      const appMetadata = user.app_metadata || {};
      let avatarUrl = null;
      if (metadata.avatar_url) avatarUrl = metadata.avatar_url;
      else if (metadata.picture) avatarUrl = metadata.picture;
      else if (appMetadata.avatar_url) avatarUrl = appMetadata.avatar_url;
      let displayName = 'User';
      if (metadata.full_name) displayName = metadata.full_name;
      else if (metadata.name) displayName = metadata.name;
      else if (user.email) displayName = user.email.split('@')[0];
      setUserProfile({
        name: displayName,
        email: user.email || 'No email',
        avatar: avatarUrl,
        provider: appMetadata.provider || 'email'
      });
    }
  }, [user]);

  const handleAvatarError = () => setAvatarError(true);
  
  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const Avatar = () =>
    userProfile?.avatar && !avatarError ? (
      <img
        src={userProfile.avatar}
        alt="Profile"
        className="sb-avatar-image"
        onError={handleAvatarError}
        referrerPolicy="no-referrer"
      />
    ) : userProfile?.name && userProfile.name !== 'User' ? (
      <div className="sb-avatar-initials">{getInitials(userProfile.name)}</div>
    ) : (
      <UserIcon className="sb-avatar-icon" />
    );

  return (
    <div className="dashboard-sidebar-profile">
      <button className="sidebar-profile-trigger" onClick={onProfileClick}>
        <span className="sb-avatar-wrap">
          <Avatar />
        </span>
        <span className="sb-profile-name">{userProfile?.name}</span>
      </button>
    </div>
  );
}

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const { 
    hasFeatureAccess, 
    getSubscriptionStatus,
    isExpired,
    hasEverSubscribed,
    loading: subscriptionLoading 
  } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState('general');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('this-month');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [quoteFilterFromGraph, setQuoteFilterFromGraph] = useState(null);
  const [featureLockModal, setFeatureLockModal] = useState({
    isOpen: false,
    featureName: '',
    reason: ''
  });

  const [modalState, setModalState] = useState({
    action: null,
    id: null
  });

  useEffect(() => {
    const fullPath = location.pathname;
    const pathWithoutPrefix = fullPath.replace('/dashboard/', '').replace('/dashboard', '');
    const pathParts = pathWithoutPrefix.split('/').filter(Boolean);
    const mainPath = pathParts[0] || '';
    
    if (pathParts.length > 1) {
      const secondPart = pathParts[1];
      
      if (mainPath === 'team') {
        if (secondPart === 'add-employee') {
          setActiveTab('EmployeeDashboard');
          setModalState({ action: 'add', id: null });
        } else if (secondPart.startsWith('view-')) {
          const id = secondPart.replace('view-', '');
          setActiveTab('EmployeeDetails');
          setSelectedEmployeeId(id);
          setModalState({ action: 'view', id });
        } else if (secondPart.startsWith('edit-')) {
          const id = secondPart.replace('edit-', '');
          setActiveTab('EmployeeDetails');
          setSelectedEmployeeId(id);
          setModalState({ action: 'edit', id });
        } else {
          setActiveTab('EmployeeDetails');
          setSelectedEmployeeId(secondPart);
          setModalState({ action: 'view', id: secondPart });
        }
        return;
      }
      
      const tabId = URL_TAB_MAP[mainPath] || 'general';
      
      if (secondPart.startsWith('add-')) {
        setActiveTab(tabId);
        setModalState({ action: 'add', id: null });
      } else if (secondPart.startsWith('view-')) {
        const slug = secondPart.replace('view-', '');
        const actualId = getIdFromSlug(slug, mainPath);
        setActiveTab(tabId);
        setModalState({ action: 'view', id: actualId, slug });
      } else if (secondPart.startsWith('edit-')) {
        const slug = secondPart.replace('edit-', '');
        const actualId = getIdFromSlug(slug, mainPath);
        setActiveTab(tabId);
        setModalState({ action: 'edit', id: actualId, slug });
      }
    } else {
      const tabId = URL_TAB_MAP[mainPath] || 'general';
      setActiveTab(tabId);
      setSelectedEmployeeId(null);
      setModalState({ action: null, id: null });
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setMobileMenuOpen(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        !event.target.closest('.dashboard-sidebar') &&
        !event.target.closest('.mobile-menu-toggle')
      ) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Handle sector click from graphs (for both Invoice and Quote analytics)
  const handleSectorClick = (tab, status) => {
    if (tab === 'QuotationTracker' && status) {
      setQuoteFilterFromGraph(status);
      handleTabChange('QuotationTracker');
    } else if (tab === 'invoice-tracking' && status) {
      setStatusFilter(status);
      handleTabChange('invoice-tracking');
    }
  };

  const handleTabChange = (tabId) => {
    const status = getSubscriptionStatus();
    
    // Always allow dashboard and profile
    if (tabId === 'general' || tabId === 'user-profile-setup') {
      setActiveTab(tabId);
      setSelectedEmployeeId(null);
      setModalState({ action: null, id: null });
      const urlPath = TAB_URL_MAP[tabId] || 'dashboard';
      navigate(`/dashboard/${urlPath}`);
      if (isMobile) setMobileMenuOpen(false);
      return;
    }

    // Check feature access for all other tabs
    if (!hasFeatureAccess(tabId)) {
      let reason = 'This feature is only available on the Premium plan.';
      let featureName = tabs.find(t => t.id === tabId)?.label || 'This feature';
      
      if (status === 'expired') {
        reason = `Your subscription has expired. Renew to access ${featureName}.`;
      } else if (status === 'inactive') {
        reason = `Your subscription is inactive. Reactivate to access ${featureName}.`;
      }
      
      setFeatureLockModal({
        isOpen: true,
        featureName: featureName,
        reason: reason
      });
      return;
    }

    setActiveTab(tabId);
    setSelectedEmployeeId(null);
    setModalState({ action: null, id: null });
    const urlPath = TAB_URL_MAP[tabId] || 'dashboard';
    navigate(`/dashboard/${urlPath}`);
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleProfileClick = () => {
    handleTabChange('user-profile-setup');
  };

  const switchToCreateInvoiceTab = () => {
    handleTabChange('invoice-generation');
  };

  const switchToInvoiceTrackerTab = () => {
    handleTabChange('invoice-tracking');
  };

  const createSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  };
 
  const openModal = (action, idOrData = null) => {
    const currentPath = TAB_URL_MAP[activeTab] || 'dashboard';
    
    const singularMap = {
      'clients': 'client',
      'expenses': 'expense',
      'invoice-items': 'item',
      'track-invoices': 'invoice',
      'track-quotes': 'quote',
      'earnings': 'income',
      'team': 'employee',
      'projects': 'project'
    };
    
    let suffix = '';
    
    if (action === 'add') {
      const singular = singularMap[currentPath] || 'item';
      suffix = `add-${singular}`;
    } else if ((action === 'view' || action === 'edit') && idOrData) {
      let slugPart = '';
      
      if (typeof idOrData === 'object' && idOrData !== null) {
        if (idOrData.quotationNumber && idOrData.clientName) {
          slugPart = `${createSlug(idOrData.quotationNumber)}-${createSlug(idOrData.clientName)}`;
        }
        else if (idOrData.invoiceNumber && idOrData.clientName) {
          slugPart = `${createSlug(idOrData.invoiceNumber)}-${createSlug(idOrData.clientName)}`;
        }
        else if (idOrData.name) {
          slugPart = createSlug(idOrData.name);
        }
        else if (idOrData.id) {
          slugPart = idOrData.id;
        }
        
        if (idOrData.id && slugPart) {
          sessionStorage.setItem(`modal_${currentPath}_${slugPart}`, idOrData.id);
        }
      } else {
        slugPart = idOrData;
      }
      
      suffix = `${action}-${slugPart}`;
    }
    
    if (suffix) {
      const newUrl = `/dashboard/${currentPath}/${suffix}`;
      navigate(newUrl);
    }
    
    if (isMobile) setMobileMenuOpen(false);
  };

  const getIdFromSlug = (slug, path) => {
    const storedId = sessionStorage.getItem(`modal_${path}_${slug}`);
    if (storedId) return storedId;
    return slug;
  };

  const closeModal = () => {
    const currentPath = TAB_URL_MAP[activeTab] || 'dashboard';
    const newUrl = `/dashboard/${currentPath}`;
    navigate(newUrl);
  };

  const handleEmployeeSelect = (employeeId, employeeName) => {
    const slug = employeeName 
      ? employeeName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      : employeeId;
    
    setSelectedEmployeeId(employeeId);
    setActiveTab('EmployeeDetails');
    navigate(`/dashboard/team/view-${slug || employeeId}`);
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleBackToTeam = () => {
    setSelectedEmployeeId(null);
    handleTabChange('EmployeeDashboard');
  };

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);

  // Determine which features are locked based on subscription status
  const isFeatureLocked = (tabId) => {
    // Dashboard and Profile are always accessible
    if (tabId === 'general' || tabId === 'user-profile-setup') {
      return false;
    }
    
    // All other features require subscription or are part of free plan
    return !hasFeatureAccess(tabId);
  };

  const tabs = [
    { id: 'general', label: 'Dashboard', icon: <BarChart size={20} />, alwaysFree: true },
    { id: 'invoice-generation', label: 'Create Invoice', icon: <FileText size={20} />, alwaysFree: false },
    { id: 'invoice-tracking', label: 'Track Invoices', icon: <ClipboardList size={20} />, alwaysFree: false },
    { id: 'client-management', label: 'Clients', icon: <UserCircle size={20} />, alwaysFree: false },
    { id: 'ProjectDashboard', label: 'Projects', icon: <FolderKanban size={20}/>, alwaysFree: false },
    { id: 'Quotations', label: 'Create Quote', icon: <FileText size={20} />, alwaysFree: false },
    { id: 'QuotationTracker', label: 'Track Quotes', icon: <ClipboardListIcon size={20} />, alwaysFree: false },
    { id: 'Earnings', label: 'Earnings', icon: <IndianRupee size={20} />, alwaysFree: false },
    { id: 'expense-tracker', label: 'Expenses', icon: <Wallet size={20} />, alwaysFree: false },
    { id: 'item-management', label: 'Invoice Items', icon: <Package size={20} />, alwaysFree: false },
    { id: 'EmployeeDashboard', label: 'Team', icon: <Users size={20}/>, alwaysFree: false },
  ];

  const handlePeriodChange = (e) => setSelectedPeriod(e.target.value);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <GeneralDashboard
            user={user}
            onSectorClick={handleSectorClick}
            onSwitchToTracker={switchToInvoiceTrackerTab}
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
          />
        );
      case 'user-profile-setup':
        return (
          <div className="dashboard-tab-content">
            <UserProfileSetup />
          </div>
        );
      case 'invoice-generation':
        return (
          <div className="dashboard-tab-content">
            <InvoiceForm />
          </div>
        );
      case 'invoice-tracking':
        return (
          <div className="dashboard-tab-content">
            <ClientInvoiceTracker 
              onSwitchToCreateTab={switchToCreateInvoiceTab} 
              sectorStatus={statusFilter}
              modalState={modalState}
              openModal={openModal}
              closeModal={closeModal}
            />
          </div>
        );
      case 'Earnings':
        return (
          <div className="dashboard-tab-content">
            <ManualIncomeForm 
              modalState={modalState}
              openModal={openModal}
              closeModal={closeModal}
            />
          </div>
        );
      case 'item-management':
        return (
          <div className="dashboard-tab-content">
            <UserProfileItems 
              modalState={modalState}
              openModal={openModal}
              closeModal={closeModal}
            />
          </div>
        );
      case 'expense-tracker':
        return (
          <div className="dashboard-tab-content">
            <Expenses 
              modalState={modalState}
              openModal={openModal}
              closeModal={closeModal}
            />
          </div>
        );
      case 'client-management':
        return (
          <div className="dashboard-tab-content">
            <Clients 
              modalState={modalState}
              openModal={openModal}
              closeModal={closeModal}
            />
          </div>
        );
      case 'Quotations':
        return (
          <div className="dashboard-tab-content">
            <QuotationForm/>
          </div>
        );
      case "QuotationTracker":
        return (
          <div className="dashboard-tab-content">
            <QuoteTracker
              onSwitchToCreateTab={() => handleTabChange("Quotations")}
              onClearSelection={() => setQuoteFilterFromGraph(null)}
              modalState={modalState}
              openModal={openModal}
              closeModal={closeModal}
              initialFilter={quoteFilterFromGraph}
            />
          </div>
        );
      case 'EmployeeDashboard':
        return (
          <div className="dashboard-tab-content">
            <EmployeeDashboard 
              onEmployeeSelect={handleEmployeeSelect}
              modalState={modalState}
              openModal={openModal}
              closeModal={closeModal}
            />
          </div>
        );
      case 'EmployeeDetails':
        return (
          <div className="dashboard-tab-content">
            <EmployeeDetails 
              employeeId={selectedEmployeeId} 
              onBack={handleBackToTeam}
              modalState={modalState}
              openModal={openModal}
              closeModal={closeModal}
            />
          </div>
        );
      case 'ProjectDashboard':
        return (
          <div className="dashboard-tab-content">
            <ProjectDashboard
              modalState={modalState}
              openModal={openModal}
              closeModal={closeModal}
            />
          </div>
        );
      default:
        return <GeneralDashboard user={user} onSwitchToTracker={switchToInvoiceTrackerTab} selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange} />;
    }
  };

  return (
    <div className="dashboard">
      {isMobile && (
        <button 
          className={`mobile-menu-toggle ${mobileMenuOpen ? 'mobile-menu-toggle-hidden' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Open menu"
        >
          <MenuIcon size={24} />
        </button>
      )}

      {isMobile && (
        <div
          className={`dashboard-overlay ${mobileMenuOpen ? 'dashboard-overlay-visible' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className="dashboard-container">
        <aside
          className={`dashboard-sidebar ${sidebarCollapsed ? 'dashboard-sidebar-collapsed' : ''} ${
            isMobile && mobileMenuOpen ? 'dashboard-sidebar-open' : ''
          }`}
        >
          {isMobile && (
            <button className="dashboard-mobile-close" onClick={closeMobileMenu} aria-label="Close menu">
              ✕
            </button>
          )}
          
          <div className="dashboard-sidebar-inner">
            <div className="dashboard-sidebar-header">
              <div className="dashboard-sidebar-brand">
                {!sidebarCollapsed && (
                  <div className="dashboard-brand-text">
                    <img src={logo} alt="Logo" />
                  </div>
                )}
              </div>
              {!isMobile && (
                <button
                  className="dashboard-sidebar-toggle"
                  onClick={toggleSidebar}
                  aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                </button>
              )}
            </div>

            <nav className="dashboard-sidebar-nav" tabIndex={-1}>
              <ul className="dashboard-nav-list">
                {tabs.map((tab) => {
                  const isLocked = isFeatureLocked(tab.id);
                  return (
                    <li key={tab.id} className="dashboard-nav-item">
                      <button
                        className={`dashboard-nav-link ${
                          (activeTab === tab.id || (tab.id === 'EmployeeDashboard' && activeTab === 'EmployeeDetails')) 
                            ? 'dashboard-nav-link-active' 
                            : ''
                        } ${isLocked ? 'dashboard-nav-link-locked' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                        title={sidebarCollapsed ? tab.label : ''}
                      >
                        <span className="dashboard-nav-icon">{tab.icon}</span>
                        {!sidebarCollapsed && (
                          <div className="dashboard-nav-content">
                            <span className="dashboard-nav-label">{tab.label}</span>
                            {isLocked && <Lock size={14} className="dashboard-nav-lock" />}
                          </div>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <SidebarProfile user={user} onProfileClick={handleProfileClick} />
          </div>
        </aside>

        <main className={`dashboard-main ${sidebarCollapsed ? 'dashboard-main-expanded' : ''}`}>
          <div className="dashboard-content">{renderTabContent()}</div>
        </main>
      </div>

      <FeatureLockModal
        isOpen={featureLockModal.isOpen}
        onClose={() => setFeatureLockModal({ isOpen: false, featureName: '', reason: '' })}
        featureName={featureLockModal.featureName}
        reason={featureLockModal.reason}
      />
    </div>
  );
};

const GeneralDashboard = ({ user, onSectorClick, onSwitchToTracker, selectedPeriod, onPeriodChange }) => {
  return (
    <div className="dashboard-general">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <h1 className="dashboard-title">Dashboard</h1>
        </div>
        <div className="dashboard-unified-period-bar">
          <select
            className="dashboard-unified-period-selector"
            value={selectedPeriod}
            onChange={onPeriodChange}
          >
            <option value="this-month">This Month</option>
            <option value="last-month">Last Month</option>
            <option value="last-3-months">Last 3 Months</option>
            <option value="last-6-months">Last 6 Months</option>
            <option value="this-year">This Year</option>
            <option value="last-year">Last Year</option>
            <option value="all-time">All Time</option>
          </select>
        </div>
      </div>
    
      <SubscriptionBanner />
      <DashboardMiegraph user={user} selectedPeriod={selectedPeriod} />
      <DashboardMielinegraph user={user} selectedPeriod={selectedPeriod} />
      <DashboardInvoiceCalendar user={user} onSwitchToTracker={onSwitchToTracker} />
      <InvoiceStatusTracker user={user} onSectorClick={onSectorClick} />
      <TopClientsRevenue user={user} />
      <QuoteAnalyticGraph 
        user={user}
        onSectorClick={onSectorClick}
        selectedPeriod={selectedPeriod}
      />
      <DashboardRecentActivity user={user} />
    </div>
  );
};

export default Dashboard;